import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { 
  House, 
  Upload, 
  FileArrowUp, 
  Users, 
  ListChecks, 
  BookOpen,
  SignOut,
  List,
  X,
  GraduationCap
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { path: '/admin', icon: House, label: 'Dashboard', exact: true },
  { path: '/admin/upload-students', icon: Upload, label: 'Upload Students' },
  { path: '/admin/upload-results', icon: FileArrowUp, label: 'Upload Results' },
  { path: '/admin/students', icon: Users, label: 'View Students' },
  { path: '/admin/results', icon: ListChecks, label: 'View Results' },
  { path: '/admin/programs', icon: BookOpen, label: 'Programs' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#FEF7F7]">
      {/* Mobile Header */}
      <header className="lg:hidden bg-white border-b border-[#E8E8E8] p-3 sm:p-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <GraduationCap size={24} className="sm:w-7 sm:h-7 flex-shrink-0" weight="duotone" className="text-[#E31E25]" />
          <span className="font-bold text-[#1A1A1A] text-sm sm:text-base truncate" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            EDSolutions 360
          </span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-[#FEF7F7] transition-colors rounded-md"
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </header>

      {/* Sidebar */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#E8E8E8]
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          overflow-y-auto
        `}
      >
        {/* Sidebar Header */}
        <div className="p-4 sm:p-6 border-b border-[#E8E8E8]">
          <div className="flex items-center gap-3">
            <GraduationCap size={32} weight="duotone" className="text-[#E31E25] flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="font-bold text-[#1A1A1A] text-sm truncate" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                EDSolutions 360
              </h1>
              <p className="text-xs text-[#666666]">Admin Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 sm:p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium transition-colors rounded-sm ${
                  isActive
                    ? 'bg-[#E31E25] text-white'
                    : 'text-[#666666] hover:bg-[#FEF7F7] hover:text-[#1A1A1A]'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon size={20} weight="duotone" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 border-t border-[#E8E8E8] bg-white">
          <div className="mb-2 sm:mb-3 px-3 sm:px-4">
            <p className="text-xs text-[#666666]">Signed in as</p>
            <p className="text-sm font-medium text-[#1A1A1A] truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium text-[#E31E25] hover:bg-[#E31E25]/10 w-full transition-colors rounded-sm"
          >
            <SignOut size={20} weight="duotone" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-0 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
